import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useWalletUI } from "@/components/Wallet";
import { Cursor } from "@/components/Typewriter";

// Interactive MIZUKARA terminal — command-line interface (frontend only).
export default function InteractiveTerminal({ className = "", height = "h-[460px]" }) {
  const { t, lang } = useLanguage();
  const { openModal } = useWalletUI();
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // re-seed welcome on language change
  useEffect(() => {
    setHistory(t("cli.welcome").map((text) => ({ type: "out", text })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [history]);

  const runCommand = (raw) => {
    const cmd = raw.trim().toLowerCase();
    if (!cmd) return;
    const echo = { type: "in", text: `> ${raw.trim()}` };

    if (cmd === "clear") {
      setHistory([]);
      return;
    }
    if (cmd === "connect") {
      setHistory((h) => [...h, echo, ...t("cli.connect").map((text) => ({ type: "out", text }))]);
      setTimeout(openModal, 500);
      return;
    }
    const known = ["help", "status", "network", "agent", "lore"];
    if (known.includes(cmd)) {
      setHistory((h) => [...h, echo, ...t(`cli.${cmd}`).map((text) => ({ type: "out", text }))]);
    } else {
      setHistory((h) => [...h, echo, { type: "out", text: t("cli.unknown") }]);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    runCommand(input);
    setInput("");
  };

  const quick = ["help", "status", "network", "agent", "lore", "connect"];

  return (
    <div
      className={`mizu-panel mizu-corners flex flex-col ${className}`}
      data-testid="interactive-terminal"
      onClick={() => inputRef.current?.focus()}
    >
      {/* title bar */}
      <div className="flex items-center justify-between border-b border-mizu-cyan/20 px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 bg-red-400/70" />
          <span className="h-2 w-2 bg-mizu-cyan/50" />
          <span className="h-2 w-2 bg-mizu-blue/50" />
          <span className="ml-2 font-mono text-[10px] tracking-[0.25em] text-mizu-cyan">
            {t("terminalPage.bootTitle")}
          </span>
        </div>
        <span className="font-mono text-[10px] text-mizu-faint">tty/001</span>
      </div>

      {/* output */}
      <div
        ref={scrollRef}
        className={`flex-1 overflow-y-auto px-4 py-3 font-mono text-[12px] leading-[1.55] ${height}`}
        data-testid="terminal-output"
      >
        {history.map((line, i) => (
          <div
            key={i}
            className={
              line.type === "in"
                ? "mt-2 text-white"
                : line.text.startsWith("[✓]")
                  ? "text-mizu-cyan/90"
                  : "whitespace-pre-wrap text-mizu-dim"
            }
          >
            {line.text}
          </div>
        ))}

        {/* input line */}
        <form onSubmit={onSubmit} className="mt-2 flex items-center gap-2">
          <span className="text-mizu-cyan">&gt;</span>
          <input
            ref={inputRef}
            data-testid="terminal-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="w-full bg-transparent font-mono text-[12px] text-white caret-mizu-cyan outline-none placeholder:text-mizu-faint/60"
            placeholder={t("terminalPage.inputPlaceholder")}
            autoComplete="off"
            spellCheck="false"
            aria-label="terminal input"
          />
          <Cursor />
        </form>
      </div>

      {/* quick commands */}
      <div className="flex flex-wrap items-center gap-2 border-t border-mizu-cyan/15 px-4 py-2.5">
        <span className="font-mono text-[9px] tracking-[0.25em] text-mizu-faint">
          {t("terminalPage.quickCommands")}
        </span>
        {quick.map((c) => (
          <button
            key={c}
            data-testid={`terminal-cmd-${c}`}
            onClick={(e) => {
              e.stopPropagation();
              runCommand(c);
            }}
            className="mizu-btn border border-mizu-cyan/25 px-2 py-0.5 font-mono text-[10px] text-mizu-dim hover:text-mizu-cyan"
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}
